import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Upload, X, Plus, UserPlus, Mail, KeyRound, Trash2, Truck, Pencil, ShieldPlus } from "lucide-react";
import { QualificationsDialog } from "@/components/QualificationsDialog";
import { VolunteerContactDialog } from "@/components/VolunteerContactDialog";
import { SanitariList } from "@/components/SanitariList";
import { volunteerQuals } from "@/lib/quals";
import { TypeColorsEditor } from "@/components/TypeColorsEditor";

export default function AdminPage() {
  const { user } = useAuth();
  const { settings, setSettings, refresh } = useSettings();
  const navigate = useNavigate();
  const [local, setLocal] = useState(settings);
  const [volunteers, setVolunteers] = useState([]);
  const [newType, setNewType] = useState("");
  const [newQual, setNewQual] = useState("");
  const [volForm, setVolForm] = useState({ name: "", email: "", phone: "", password: "", mode: "password", qualifications: [] });
  const [savingVol, setSavingVol] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehForm, setVehForm] = useState({ name: "", type: "", plate: "", requires_autista: true, is_ambulance: false });
  const [editingVeh, setEditingVeh] = useState(null);
  const [savingVeh, setSavingVeh] = useState(false);
  const [editingQuals, setEditingQuals] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => { setLocal(settings); }, [settings]);
  useEffect(() => {
    if (!user.is_admin) return;
    api.get("/admin/volunteers").then(({ data }) => setVolunteers(data)).catch(() => {});
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, [user.is_admin]);

  useEffect(() => {
    if (!user.is_admin) navigate("/");
  }, [user.is_admin, navigate]);

  if (!user.is_admin) return null;

  const setL = (k, v) => setLocal((p) => ({ ...p, [k]: v }));

  const save = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch);
      setSettings((p) => ({ ...p, ...data }));
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800 * 1024) { toast.error("Immagine troppo grande (max 800KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setL("logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const addToList = (key, val, setter) => {
    const v = val.trim();
    if (!v || local[key].includes(v)) return;
    setL(key, [...local[key], v]);
    setter("");
  };
  const removeFromList = (key, val) => setL(key, local[key].filter((x) => x !== val));

  const toggleVolQual = (q) =>
    setVolForm((p) => ({ ...p, qualifications: p.qualifications.includes(q) ? p.qualifications.filter((x) => x !== q) : [...p.qualifications, q] }));

  const resetLink = async (v) => {
    try {
      const { data } = await api.post(`/admin/volunteers/${v.id}/reset-link`);
      try { await navigator.clipboard.writeText(data.link); } catch (e) {}
      toast.success("Link di reset copiato negli appunti", { description: data.link, duration: 12000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleCoordinatore = async (v) => {
    const promote = v.role !== "coordinatore";
    try {
      const { data } = await api.post(`/admin/volunteers/${v.id}/${promote ? "promote" : "demote"}`);
      setVolunteers((p) => p.map((x) => (x.id === v.id ? data : x)));
      toast.success(promote ? "Nominato co-coordinatore" : "Ruolo rimosso", {
        description: promote
          ? `${v.name} ora può gestire tutti i servizi.`
          : `${v.name} è di nuovo volontario.`,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteVolunteer = async (v) => {
    try {
      await api.delete(`/admin/volunteers/${v.id}`);
      setVolunteers((p) => p.filter((x) => x.id !== v.id));
      toast.success("Utente eliminato", { description: `${v.name} è stato rimosso dal comitato.` });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const createVolunteer = async (e) => {
    e.preventDefault();
    setSavingVol(true);
    try {
      const { data } = await api.post("/admin/volunteers", volForm);
      setVolunteers((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Volontario creato", {
        description: volForm.mode === "invite" ? "Email di attivazione inviata." : "Password impostata.",
      });
      setVolForm({ name: "", email: "", phone: "", password: "", mode: "password", qualifications: [] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingVol(false);
    }
  };

  const saveVehicle = async (e) => {
    e.preventDefault();
    if (!vehForm.name.trim()) { toast.error("Inserisci il nome del mezzo"); return; }
    setSavingVeh(true);
    try {
      if (editingVeh) {
        const { data } = await api.put(`/vehicles/${editingVeh}`, vehForm);
        setVehicles((p) => p.map((x) => (x.id === editingVeh ? data : x)).sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Mezzo aggiornato");
      } else {
        const { data } = await api.post("/vehicles", vehForm);
        setVehicles((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Mezzo aggiunto all'autoparco");
      }
      setVehForm({ name: "", type: "", plate: "", requires_autista: true, is_ambulance: false });
      setEditingVeh(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingVeh(false);
    }
  };

  const editVehicle = (v) => {
    setEditingVeh(v.id);
    setVehForm({ name: v.name, type: v.type || "", plate: v.plate || "", requires_autista: v.requires_autista !== false, is_ambulance: !!v.is_ambulance });
  };

  const deleteVehicle = async (v) => {
    try {
      await api.delete(`/vehicles/${v.id}`);
      setVehicles((p) => p.filter((x) => x.id !== v.id));
      if (editingVeh === v.id) { setEditingVeh(null); setVehForm({ name: "", type: "", plate: "", requires_autista: true, is_ambulance: false }); }
      toast.success("Mezzo eliminato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="cri-topbar" />
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" data-testid="admin-back" onClick={() => navigate("/")} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <BrandLogo logoUrl={settings.logo_url} size={32} />
          <div>
            <p className="font-display font-bold">Area Amministratore</p>
            <p className="text-xs text-muted-foreground">{settings.committee_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight mb-6">Amministrazione</h1>
        <Tabs defaultValue="branding">
          <TabsList className="flex-wrap h-auto" data-testid="admin-tabs">
            <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="lists" data-testid="tab-lists">Liste</TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="tab-volunteers">Volontari</TabsTrigger>
            <TabsTrigger value="autoparco" data-testid="tab-autoparco">Autoparco</TabsTrigger>
          </TabsList>

          {/* BRANDING */}
          <TabsContent value="branding" className="mt-6">
            <div className="rounded-xl border border-border bg-card p-6 space-y-6 max-w-xl">
              <div className="flex items-center gap-4">
                <BrandLogo logoUrl={local.logo_url} size={64} />
                <div className="flex gap-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted" data-testid="logo-upload-label">
                    <Upload className="h-4 w-4" /> Carica logo
                    <input type="file" accept="image/*" className="hidden" data-testid="logo-upload-input" onChange={onLogoFile} />
                  </label>
                  {local.logo_url && (
                    <Button variant="ghost" size="icon" data-testid="logo-remove" onClick={() => setL("logo_url", "")} className="rounded-full">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cname">Nome comitato</Label>
                <Input id="cname" data-testid="committee-name-input" value={local.committee_name} onChange={(e) => setL("committee_name", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cemail">Email di contatto</Label>
                <Input id="cemail" type="email" data-testid="contact-email-input" value={local.contact_email} onChange={(e) => setL("contact_email", e.target.value)} placeholder="comitato@cri.it" />
              </div>
              <Button data-testid="save-branding" onClick={() => save({ committee_name: local.committee_name, logo_url: local.logo_url, contact_email: local.contact_email })} className="rounded-full font-semibold">
                Salva branding
              </Button>
            </div>
          </TabsContent>

          {/* DASHBOARD */}
          <TabsContent value="dashboard" className="mt-6">
            <div className="rounded-xl border border-border bg-card p-6 space-y-4 max-w-xl">
              {[
                { k: "show_hero", l: "Mostra intestazione (titolo + descrizione)" },
                { k: "show_stats", l: "Mostra riquadri statistiche" },
                { k: "show_filters", l: "Mostra barra dei filtri" },
              ].map((row) => (
                <div key={row.k} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-sm font-medium">{row.l}</span>
                  <Switch data-testid={`toggle-${row.k}`} checked={!!local[row.k]} onCheckedChange={(v) => setL(row.k, v)} />
                </div>
              ))}
              <div className="py-2 border-b border-border space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Giorni di anticipo del promemoria</span>
                  <Input type="number" min="0" max="30" data-testid="reminder-days-input" value={local.reminder_days ?? 1} onChange={(e) => setL("reminder_days", Number(e.target.value))} className="w-20" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Orario di invio promemoria (0-23)</span>
                  <Input type="number" min="0" max="23" data-testid="reminder-hour-input" value={local.reminder_hour ?? 19} onChange={(e) => setL("reminder_hour", Number(e.target.value))} className="w-20" />
                </div>
                <p className="text-xs text-muted-foreground">Es: 1 giorno = promemoria inviato il giorno prima del servizio. 0 = lo stesso giorno.</p>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm font-medium">Durata link reset password (ore)</span>
                <Input type="number" min="1" max="720" data-testid="reset-hours-input" value={local.reset_link_hours ?? 24} onChange={(e) => setL("reset_link_hours", Number(e.target.value))} className="w-20" />
              </div>
              <Button data-testid="save-dashboard" onClick={() => save({ show_hero: local.show_hero, show_stats: local.show_stats, show_filters: local.show_filters, reminder_hour: Number(local.reminder_hour ?? 19), reminder_days: Number(local.reminder_days ?? 1), reset_link_hours: Number(local.reset_link_hours ?? 24) })} className="rounded-full font-semibold mt-2">
                Salva layout
              </Button>
            </div>
          </TabsContent>

          {/* LISTS */}
          <TabsContent value="lists" className="mt-6 grid md:grid-cols-2 gap-6">
            <ListEditor
              title="Tipi di servizio" testid="types" items={local.service_types}
              value={newType} onValue={setNewType}
              onAdd={() => addToList("service_types", newType, setNewType)}
              onRemove={(v) => removeFromList("service_types", v)}
              onSave={() => save({ service_types: local.service_types })}
            />
            <TypeColorsEditor settings={local} onSave={save} />
            <ListEditor
              title="Qualifiche" testid="quals" items={local.qualifications}
              value={newQual} onValue={setNewQual}
              onAdd={() => addToList("qualifications", newQual, setNewQual)}
              onRemove={(v) => removeFromList("qualifications", v)}
              onSave={() => save({ qualifications: local.qualifications })}
            />
          </TabsContent>

          {/* VOLUNTEERS */}
          <TabsContent value="volunteers" className="mt-6 grid lg:grid-cols-2 gap-6">
            <form onSubmit={createVolunteer} className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="volunteer-form">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><UserPlus className="h-5 w-5" /> Nuovo volontario</h3>
              <div className="space-y-2">
                <Label>Nome e cognome</Label>
                <Input data-testid="vol-name-input" required value={volForm.name} onChange={(e) => setVolForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" data-testid="vol-email-input" required value={volForm.email} onChange={(e) => setVolForm((p) => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Telefono <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
                <Input type="tel" data-testid="vol-phone-input" value={volForm.phone} onChange={(e) => setVolForm((p) => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Qualifiche</Label>
                <div className="grid grid-cols-2 gap-2">
                  {volunteerQuals(settings.qualifications).map((q) => (
                    <label key={q} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                      <Checkbox data-testid={`vol-qual-${q}`} checked={volForm.qualifications.includes(q)} onCheckedChange={() => toggleVolQual(q)} />
                      <span>{q}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Modalità accesso</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" data-testid="vol-mode-password" onClick={() => setVolForm((p) => ({ ...p, mode: "password" }))}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${volForm.mode === "password" ? "border-[#D32F2F] bg-accent text-[#D32F2F]" : "border-border"}`}>
                    <KeyRound className="h-4 w-4" /> Password
                  </button>
                  <button type="button" data-testid="vol-mode-invite" onClick={() => setVolForm((p) => ({ ...p, mode: "invite" }))}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${volForm.mode === "invite" ? "border-[#D32F2F] bg-accent text-[#D32F2F]" : "border-border"}`}>
                    <Mail className="h-4 w-4" /> Invito email
                  </button>
                </div>
              </div>
              {volForm.mode === "password" && (
                <div className="space-y-2">
                  <Label>Password iniziale</Label>
                  <Input type="text" data-testid="vol-password-input" value={volForm.password} onChange={(e) => setVolForm((p) => ({ ...p, password: e.target.value }))} placeholder="min 6 caratteri — da comunicare" />
                </div>
              )}
              <Button type="submit" data-testid="create-volunteer-button" disabled={savingVol} className="rounded-full font-semibold w-full">
                {savingVol ? "Creazione…" : "Crea volontario"}
              </Button>
            </form>

            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-semibold mb-4">Volontari ({volunteers.length})</h3>
              <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="volunteers-list">
                {volunteers.map((v) => (
                  <div key={v.id} data-testid={`volunteer-${v.id}`} className="rounded-lg border border-border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sm">{v.name}{v.external && <span className="ml-2 text-[10px] font-medium text-muted-foreground uppercase">non usa l'app</span>}</p>
                        <p className="text-xs text-muted-foreground">{v.email}</p>
                        {v.phone && <a href={`tel:${v.phone}`} data-testid={`volunteer-phone-${v.id}`} className="text-xs text-[#D32F2F] hover:underline">{v.phone}</a>}
                      </div>
                      {v.role === "coordinatore" && (
                        <Badge data-testid={`role-badge-${v.id}`} className="bg-primary text-primary-foreground text-[10px] shrink-0">Co-coordinatore</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {v.qualifications.map((q) => <Badge key={q} variant="secondary" className="text-[10px]">{q}</Badge>)}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button size="sm" variant="outline" data-testid={`admin-edit-contact-${v.id}`} onClick={() => setEditingContact(v)} className="rounded-full text-xs h-7">
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Anagrafica
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`admin-edit-quals-${v.id}`} onClick={() => setEditingQuals(v)} className="rounded-full text-xs h-7">
                        <ShieldPlus className="h-3.5 w-3.5 mr-1" /> Qualifiche
                      </Button>
                      {!v.external && (
                        <>
                          <Button size="sm" variant="outline" data-testid={`reset-link-${v.id}`} onClick={() => resetLink(v)} className="rounded-full text-xs h-7">
                            Link reset password
                          </Button>
                          <Button
                            size="sm"
                            variant={v.role === "coordinatore" ? "destructive" : "default"}
                            data-testid={`toggle-coord-${v.id}`}
                            onClick={() => toggleCoordinatore(v)}
                            className="rounded-full text-xs h-7"
                          >
                            {v.role === "coordinatore" ? "Rimuovi ruolo" : "Nomina co-coordinatore"}
                          </Button>
                        </>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" data-testid={`delete-volunteer-${v.id}`} className="rounded-full text-xs h-7 text-destructive hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare {v.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              L'account verrà rimosso definitivamente insieme alle sue iscrizioni ai servizi. L'azione non è reversibile.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-${v.id}`} onClick={() => deleteVolunteer(v)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
                {volunteers.length === 0 && <p className="text-sm text-muted-foreground">Nessun volontario registrato.</p>}
              </div>
            </div>
            <SanitariList volunteers={volunteers} setVolunteers={setVolunteers} />
          </TabsContent>

          {/* AUTOPARCO */}
          <TabsContent value="autoparco" className="mt-6 grid lg:grid-cols-2 gap-6">
            <form onSubmit={saveVehicle} className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="vehicle-form">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Truck className="h-5 w-5" /> {editingVeh ? "Modifica mezzo" : "Nuovo mezzo"}</h3>
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input data-testid="veh-name-input" required value={vehForm.name} onChange={(e) => setVehForm((p) => ({ ...p, name: e.target.value }))} placeholder="MSB 01" />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Input data-testid="veh-type-input" value={vehForm.type} onChange={(e) => setVehForm((p) => ({ ...p, type: e.target.value }))} placeholder="Ambulanza, Automedica, Pulmino, Auto…" />
              </div>
              <div className="space-y-2">
                <Label>Targa (opzionale)</Label>
                <Input data-testid="veh-plate-input" value={vehForm.plate} onChange={(e) => setVehForm((p) => ({ ...p, plate: e.target.value }))} placeholder="CRI 123 AB" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <span className="text-sm font-medium">Richiede qualifica «Autista»</span>
                <Switch data-testid="veh-requires-autista" checked={vehForm.requires_autista} onCheckedChange={(v) => setVehForm((p) => ({ ...p, requires_autista: v }))} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <span className="text-sm font-medium">È un'ambulanza</span>
                <Switch data-testid="veh-is-ambulance" checked={vehForm.is_ambulance} onCheckedChange={(v) => setVehForm((p) => ({ ...p, is_ambulance: v }))} />
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="save-vehicle-button" disabled={savingVeh} className="rounded-full font-semibold">
                  {savingVeh ? "Salvataggio…" : editingVeh ? "Salva modifiche" : "Aggiungi mezzo"}
                </Button>
                {editingVeh && (
                  <Button type="button" variant="ghost" data-testid="cancel-vehicle-edit" onClick={() => { setEditingVeh(null); setVehForm({ name: "", type: "", plate: "", requires_autista: true, is_ambulance: false }); }} className="rounded-full">
                    Annulla
                  </Button>
                )}
              </div>
            </form>

            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-semibold mb-4">Autoparco ({vehicles.length})</h3>
              <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="vehicles-list">
                {vehicles.map((v) => (
                  <div key={v.id} data-testid={`vehicle-row-${v.id}`} className="rounded-lg border border-border p-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{v.name}</p>
                      <p className="text-xs text-muted-foreground">{[v.type, v.plate].filter(Boolean).join(" · ") || "—"}</p>
                      <Badge variant={v.requires_autista ? "secondary" : "outline"} className="mt-1 text-[10px]">
                        {v.requires_autista ? "Richiede Autista" : "Nessuna qualifica"}
                      </Badge>
                      {v.is_ambulance && <Badge data-testid={`vehicle-amb-badge-${v.id}`} className="mt-1 ml-1 text-[10px] bg-[#D32F2F] text-white">Ambulanza</Badge>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" data-testid={`edit-vehicle-${v.id}`} onClick={() => editVehicle(v)} className="h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`delete-vehicle-${v.id}`} className="h-8 w-8 p-0 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare {v.name}?</AlertDialogTitle>
                            <AlertDialogDescription>Il mezzo verrà rimosso dall'autoparco. I servizi già creati non saranno modificati.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-vehicle-${v.id}`} onClick={() => deleteVehicle(v)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
                {vehicles.length === 0 && <p className="text-sm text-muted-foreground">Nessun mezzo. Aggiungine uno per poterlo richiedere nei servizi.</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <QualificationsDialog volunteer={editingQuals} onOpenChange={(o) => !o && setEditingQuals(null)} onSaved={(u) => setVolunteers((p) => p.map((v) => (v.id === u.id ? { ...v, ...u } : v)))} />
      <VolunteerContactDialog volunteer={editingContact} onOpenChange={(o) => !o && setEditingContact(null)} onSaved={(u) => setVolunteers((p) => p.map((v) => (v.id === u.id ? { ...v, ...u } : v)))} />
    </div>
  );
}

function ListEditor({ title, testid, items, value, onValue, onAdd, onRemove, onSave }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="font-display text-lg font-semibold mb-4">{title}</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {items.map((it) => (
          <span key={it} data-testid={`${testid}-item-${it}`} className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground border border-border px-3 py-1 text-sm">
            {it}
            <button onClick={() => onRemove(it)} data-testid={`${testid}-remove-${it}`} className="hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`${testid}-input`} value={value} onChange={(e) => onValue(e.target.value)} placeholder="Aggiungi…" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }} />
        <Button type="button" variant="outline" size="icon" data-testid={`${testid}-add`} onClick={onAdd} className="rounded-full shrink-0"><Plus className="h-4 w-4" /></Button>
      </div>
      <Button data-testid={`${testid}-save`} onClick={onSave} className="rounded-full font-semibold mt-4">Salva</Button>
    </div>
  );
}
